import { useParams, Link } from "react-router-dom";
import { Globe, ArrowLeft, Server, Cloud, ShieldCheck, Waypoints, FolderKanban, ExternalLink, Pencil, RefreshCw } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { PageHeader, StatusBadge, Panel, AsyncBoundary, EmptyState, DataTable } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SiteDomainsPanel } from "@/components/domains/SiteDomainsPanel";

export default function DomainDetail() {
  const { domain } = useParams();
  const { data: d, loading, error } = useAsync(() => api.getDomain(domain), [domain]);

  const info = d && [
    ["Projeto", d.project || "Unknown"], ["Cliente", d.client || "Unknown"], ["Servidor", d.server || "Unknown"],
    ["Environment", d.environment || "Unknown"], ["DNS", d.cloudflare || "Não configurado"],
    ["Proxy", d.proxy_status || "Unknown"], ["SSL", d.ssl_status || "Unknown"],
  ];

  return (
    <div className="space-y-6 animate-fade-up">
      <Link to="/dominios" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground" data-testid="domain-back">
        <ArrowLeft className="h-4 w-4" /> Voltar para Sites & Domínios
      </Link>

      <AsyncBoundary loading={loading} error={error}>
        {!d ? (
          <EmptyState icon={Globe} title="Domínio não encontrado" />
        ) : (
          <>
            <div className="flex flex-col gap-4 rounded-xl border border-line bg-surface p-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand/10 text-brand"><Globe className="h-6 w-6" /></div>
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h1 className="font-heading text-2xl font-bold text-foreground">{d.domain}</h1>
                    <StatusBadge status={d.proxy_status || "unknown"} label={d.proxy_status || "Unknown"} size="sm" />
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><FolderKanban className="h-3 w-3" />{d.project}</span>
                    <span className="inline-flex items-center gap-1"><Server className="h-3 w-3" />{d.server || "Unknown"}</span>
                    <span className="inline-flex items-center gap-1"><Cloud className="h-3 w-3" />{d.cloudflare || "Não configurado"}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="outline" disabled className="border-line bg-transparent" data-testid="domain-edit"><Pencil className="h-4 w-4" /> Editar indisponível</Button>
                <Button variant="outline" onClick={() => window.open(`https://${d.domain}`, "_blank", "noopener,noreferrer")} className="border-line bg-transparent hover:bg-white/5"><ExternalLink className="h-4 w-4" /> Visitar</Button>
              </div>
            </div>

            <Tabs defaultValue="overview">
              <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-line bg-surface p-1">
                {[["overview", "Overview"], ["dns", "DNS"], ["proxy", "Proxy / NPM"], ["ssl", "SSL"], ["projeto", "Projeto"]].map(([v, l]) => (
                  <TabsTrigger key={v} value={v} data-testid={`domain-tab-${v}`} className="data-[state=active]:bg-brand data-[state=active]:text-white">{l}</TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="overview" className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
                <Panel title="Visão Consolidada" className="lg:col-span-2" bodyClassName="grid gap-3 sm:grid-cols-2">
                  {info.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between rounded-lg border border-line bg-white/[0.02] px-3 py-2.5">
                      <span className="text-xs text-muted-foreground">{k}</span>
                      <span className="font-mono text-xs font-medium text-foreground">{v}</span>
                    </div>
                  ))}
                </Panel>
                <Panel title="Ações" bodyClassName="space-y-2">
                  <Button variant="outline" disabled className="w-full justify-start border-line" data-testid="domain-manage-dns"><Cloud className="h-4 w-4" /> DNS não configurado</Button>
                  <Button variant="outline" disabled className="w-full justify-start border-line" data-testid="domain-manage-proxy"><Waypoints className="h-4 w-4" /> Proxy somente leitura</Button>
                  <Button variant="outline" disabled className="w-full justify-start border-line" data-testid="domain-renew-ssl"><RefreshCw className="h-4 w-4" /> Renovação indisponível</Button>
                </Panel>
                {d.site_id && <SiteDomainsPanel siteId={d.site_id} />}
              </TabsContent>

              <TabsContent value="dns" className="mt-5 space-y-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">Registros principais deste domínio.</p>
                  <Link to="/cloudflare"><Button variant="outline" className="border-line bg-transparent hover:bg-white/5" data-testid="domain-dns-cf"><Cloud className="h-4 w-4" /> Abrir no Cloudflare</Button></Link>
                </div>
                <DataTable
                  columns={[
                    { key: "type", header: "Tipo", render: (r) => <span className="rounded border border-line px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">{r.type}</span> },
                    { key: "name", header: "Nome", render: (r) => <span className="font-mono text-xs">{r.name}</span> },
                    { key: "content", header: "Conteúdo", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.content}</span> },
                    { key: "proxy", header: "Proxy", render: (r) => <StatusBadge status={r.proxy ? "warning" : "info"} label={r.proxy ? "Proxied" : "DNS Only"} size="sm" pulse={false} /> },
                  ]}
                  rows={[]}
                  testid="domain-dns-table" />
              </TabsContent>

              <TabsContent value="proxy" className="mt-5">
                <Panel title="Proxy / Nginx Proxy Manager" icon={Waypoints} bodyClassName="space-y-3 text-sm">
                  {[["Forward host", d.forward_host || "Unknown"], ["Servidor", d.server || "Unknown"], ["Force SSL", d.force_ssl == null ? "Unknown" : d.force_ssl ? "Sim" : "Não"], ["Estado", d.adoption_state || "Unknown"]].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-line/60 pb-2 last:border-0"><span className="text-muted-foreground">{k}</span><span className="font-mono font-medium text-foreground">{v}</span></div>
                  ))}
                  <Link to="/npm"><Button variant="outline" className="mt-2 border-line bg-transparent hover:bg-white/5">Abrir no NPM</Button></Link>
                </Panel>
              </TabsContent>

              <TabsContent value="ssl" className="mt-5">
                <Panel title="Certificado SSL" icon={ShieldCheck} bodyClassName="space-y-3 text-sm">
                  {[["Emissor", d.certificate_provider || "Unknown"], ["Status", d.ssl_status || "Unknown"], ["Expira em", d.ssl_expires_at || "Unknown"], ["Renovação", "Não configurada"]].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-line/60 pb-2 last:border-0"><span className="text-muted-foreground">{k}</span><span className="font-medium text-foreground">{v}</span></div>
                  ))}
                  <Button variant="outline" disabled className="mt-2 border-line"><RefreshCw className="h-4 w-4" /> Renovação indisponível</Button>
                </Panel>
              </TabsContent>

              <TabsContent value="projeto" className="mt-5">
                <Panel title="Projeto vinculado" icon={FolderKanban} bodyClassName="space-y-3 text-sm">
                  {[["Projeto", d.project], ["Cliente", d.client], ["Servidor", d.server]].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-line/60 pb-2 last:border-0"><span className="text-muted-foreground">{k}</span><span className="font-medium text-foreground">{v}</span></div>
                  ))}
                  {d.project_id ? <Link to={`/projetos/${d.project_id}`}><Button variant="outline" className="mt-2 border-line bg-transparent hover:bg-white/5">Abrir projeto</Button></Link> : <span className="text-xs text-muted-foreground">Sem projeto adotado</span>}
                </Panel>
              </TabsContent>
            </Tabs>
          </>
        )}
      </AsyncBoundary>
    </div>
  );
}
